import { Injectable, inject } from "@angular/core";
import {Actions} from "@ngrx/effects";
import {select, Store} from "@ngrx/store";
import {exhaustMap, finalize, mergeMap, Observable, withLatestFrom} from "rxjs";
import {catchError, map} from "rxjs/operators";
import {ILocalPageOutputDto} from "@shared/interfaces/orga-response.interface";
import {TeamIssuesActions} from "@team/pages/issues/store/team-issue.actions-type";
import {
  FileDto,
  FilterDto,
  FilterOperators,
  IssueContentType,
  IssueListDto,
  IssueTemplateListDto,
  IssueType,
  Orders
} from "@server-models";
import {Router} from "@angular/router";
import {SharedImageService} from "@shared/services/shared-image.service";
import {SharedCameraService} from "@shared/services/shared-camera.service";
import {SharedModalLoaderService} from "@shared/services/shared-modal-loader.service";
import {TranslateService} from "@ngx-translate/core";
import {SharedIssueBaseService} from "@shared/components/issues/services/shared-issue-base.service";
import {SharedIssueApiBaseEffects} from "@shared/components/issues/store/shared-issue-api-base.effects";
import {SharedIssueBaseActions} from "@shared/components/issues/store/shared-issue-base.action-type";
import {TIssuePreview} from "@shared/components/issues/store/shared-issue-base.actions";
import {TeamIssueService} from "@team/pages/issues/services/team-issue.service";
import {SharedIssueService} from "@shared/components/issues/services/shared-issue.service";
import {TeamIssueSelectors} from "@team/pages/issues/store/team-issue.selector-type";
import {FormGroup} from "@angular/forms";
import {IIssueService} from "@shared/components/issues/interfaces/issue-service.interface";
import {TSubmitIssueDto} from "@shared/components/issues/types/submit-issue-dto.type";
import {SharedCacheService} from "@shared/services/cache/shared-cache.service";
import {SharedFileApiService} from "@shared/services/file/shared-file-api.service";
import {ILocalIssueDetailData} from "@shared/components/issues/interfaces/local-issue-detail-data.interface";
import {SharedIssueApiService} from "@shared/components/issues/services/shared-issue-api.service";
import {ISharedLocalBodyRequest} from "@shared/interfaces/shared-local-body-request.interface";
import {TechIssuesActions} from "@tech/pages/issues/store/tech-issue.actions-type";

@Injectable({
  providedIn: 'root'
})
export class TeamIssueApiEffects extends SharedIssueApiBaseEffects {
  private _teamIssuesService = inject(TeamIssueService);
  private _sharedFileApiService = inject(SharedFileApiService);


  constructor() {
    const actions$ = inject(Actions);
    const store = inject(Store);
    const _cameraService = inject(SharedCameraService);
    const _sharedModalLoaderService = inject(SharedModalLoaderService);
    const _translateService = inject(TranslateService);
    const _imageService = inject(SharedImageService);
    const _issuesBaseService = inject(SharedIssueBaseService);
    const _sharedIssueApiService = inject(SharedIssueApiService);
    const _issueService = inject(SharedIssueService);
    const _cacheService = inject(SharedCacheService);
    const _router = inject(Router);

    super(
      actions$,
      store,
      _cameraService,
      _sharedModalLoaderService,
      _translateService,
      _imageService,
      _issuesBaseService,
      _sharedIssueApiService,
      _issueService,
      _cacheService,
      _router
    );
  }

  override requestGetPaginated(action: ISharedLocalBodyRequest): Observable<(ILocalPageOutputDto<IssueListDto[]> | any)> {
    const body: FilterDto[] = [
      ...action.filters,
      {
        property: "issueType",
        value: IssueType.Mission.toString(),
        operator: FilterOperators.NotEqual
      }
    ];

    const options = {
      params: {
        PageSize: action.pagination.pageSize.toString(),
        PageNumber: action.pagination.pageNumber.toString(),
        Cols: action.pagination.cols,
        SortField: "lastModified",
        Sort: Orders.Descending.toString()
      }
    }
    return this._sharedIssueApiService.getAllIssueList(body, options).pipe(
      map((data: ILocalPageOutputDto<IssueListDto[]>) =>
        action.isRefresh
          ? TeamIssuesActions.getIssueListPaginatedRefresh({ data })
          : TeamIssuesActions.getIssueListPaginatedSuccess({ data })
      ),
      catchError((error) => [TeamIssuesActions.getIssueListPaginatedFail({ error })])
    )
  }

  override requestGetTemplate(templateId: number, resourceId?: number, filters: FilterDto[] = [],
                              pageNumber?: number): Observable<(ILocalPageOutputDto<IssueTemplateListDto[]> | any)> {
    const body = [
      ...filters,
      {
        property: "contentType",
        value: IssueContentType.Mission.toString(),
        operator: FilterOperators.NotEqual
      },
      {
        property: "contentType",
        value: IssueContentType.Order.toString(),
        operator: FilterOperators.NotEqual
      }
    ];

    return this._sharedIssueApiService.getIssueTemplateList(body, pageNumber).pipe(
      map((data: ILocalPageOutputDto<IssueTemplateListDto[]>) =>
        TeamIssuesActions.getIssueTemplateListSuccess({ data, templateId })
      ),
      catchError((error) => [TeamIssuesActions.getIssueTemplateListFail({ error })])
    );
  }

  override formatReadDetailDataFinish(action$: Observable<{
    detailDataFormatted: ILocalIssueDetailData
  }>): Observable<any> {
    return action$;
  }

  override requestByIdByIssueType(action: { issueId: number, issueType: IssueType }): Observable<any> {
    const apiService: IIssueService = this._teamIssuesService.getApiServiceByIssueType(action.issueType);
    return apiService.getIssue(action.issueId).pipe(
      map((data) =>
        TeamIssuesActions.getByIdSuccess({ data, cacheGuid: this._cacheService.generateGuid() })
      ),
      catchError((error) => [TeamIssuesActions.getByIdFail({ error })])
    );
  }

  override saveSignatureRequest(tenantId: number, blob: Blob, dataUrl: string, fileType: string,
                                index: number | string): Observable<{
    signature: FileDto
  } | any> {
    return this._sharedFileApiService.upload(tenantId, blob, fileType).pipe(
      exhaustMap((data: FileDto) => {
          return [TeamIssuesActions.saveSignatureSuccess({
            id: data.fileId!,
            dataUrl,
            index
          })]
        }
      ),
      catchError((error) => [TeamIssuesActions.saveSignatureFail({ error })])
    )
  }

  override savePictureRequest(tenantId: number, blob: Blob, fileType: string, pictureId: number): Observable<{
    image: FileDto
  } | any> {
    return this._sharedFileApiService.upload(tenantId, blob, fileType).pipe(
      exhaustMap((data: FileDto) => {
          return [TeamIssuesActions.savePictureSuccess({ fileId: data.fileId!, path: data.path!, pictureId })]
        }
      ),
      catchError((error) => [TeamIssuesActions.savePictureFail({ error })])
    )
  }

  override requestIssuePreviewByContentType(action: TIssuePreview): Observable<(unknown | any)> {
    const apiService: IIssueService = this._teamIssuesService.getApiServiceByIssueContentType(action.issueType);
    return apiService.getIssuePreview(action.templateId).pipe(
      map((data) =>
        TeamIssuesActions.getIssuePreviewSuccess({
          data,
          templateId: action.templateId,
          resourceId: action.resourceId,
          contentType: action.issueType
        })
      ),
      catchError((error) => [TechIssuesActions.getIssuePreviewFail({ error })])
    )
  }

  override prepareFormsToSendTemplatePreview(action$: Observable<{
    form: FormGroup,
    templatePreviewDto: any
  }>): Observable<any> {
    return action$.pipe(
      withLatestFrom(this.store.pipe((select(TeamIssueSelectors.selectCurrentPreviewIssueType)))),
      mergeMap(([{ form, templatePreviewDto }, contentType]) => {
        const service = this._issueService.getIssueFormService(contentType);
        const submitReady = service.prepareFormIssueDto(form, templatePreviewDto);
        return [TeamIssuesActions.prepareFormsToSendTemplatePreviewDone({ submitReady })]
      })
    );
  }

  override sendTemplatePreview(action$: Observable<{
    issuePrepared: TSubmitIssueDto
  }>): Observable<void> {
    return action$.pipe(
      withLatestFrom(this.store.pipe(select(TeamIssueSelectors.selectCurrentPreviewId))),
      withLatestFrom(this.store.pipe((select(TeamIssueSelectors.selectCurrentPreviewIssueType)))),
      mergeMap(([[{ issuePrepared }, templateId], contentType]) => {
        return this.sendTemplatePreviewByIssueType({
          templateId,
          issuePrepared,
          issueContentType: contentType
        });
      })
    );
  }

  override sendTemplatePreviewByIssueType(action: {
    templateId: number,
    issuePrepared: TSubmitIssueDto
    issueContentType: IssueContentType
  }): Observable<(unknown | any)> {
    const apiService = this._teamIssuesService.getApiServiceByIssueContentType(action.issueContentType);
    return apiService.sendIssueTemplatePreviewById(action.templateId, action.issuePrepared).pipe(
      map(() => TeamIssuesActions.sendTemplatePreviewSuccess({})),
      catchError(error => [TeamIssuesActions.sendTemplatePreviewFail({ error })]),
      finalize(() => {
        this._sharedModalLoaderService.dismiss();
      })
    );
  }
}
